import { useState } from 'react';
import type { AnswerDecisionBody, OnchainEntityCandidate } from '@ni/shared';
import { useAnswerDecision, useDecision } from '../../api/hooks';
import { loadErrorLine, standingAnswerLine, standingAnswerOf } from '../../api/errors';
import {
  ONCHAIN_PICK_ASK,
  ONCHAIN_PICK_CHOOSE,
  ONCHAIN_PICK_NONE,
  ONCHAIN_PICK_RECOMMENDED,
} from '../../lib/copy';
import { EmptyLine } from '../ui/empty-line';
import { ErrorLine } from '../ui/error-line';
import { Loading } from '../ui/loading';
import { WorkingButton } from '../ui/working-button';

/**
 * **"Which of these on-chain identities, if any, is this person?"**
 * (docs/notes/2026-10-01-onchain-intel-arkham-spec.md §4.3).
 *
 * One identity or none, in the pipeline's order. A recommended one is the
 * candidate a link corroborated, never one matched by name alone, and it is
 * still the office's choice. The brief waits on this answer with any findings
 * on the same run. Not identity: choosing writes which identity's wallets
 * research reads, and "None of these" stops the same ones being offered again.
 */
export function OnchainEntityPick({
  subjectId,
  decisionId,
  runId,
}: {
  subjectId: string;
  decisionId: string;
  runId: string;
}) {
  const decision = useDecision(decisionId);
  const answer = useAnswerDecision(subjectId);
  const [pending, setPending] = useState<string | null>(null);

  if (decision.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (decision.isError) return <EmptyLine>{loadErrorLine(decision.error)}</EmptyLine>;
  if (decision.data.kind !== 'onchain_entity_pick') return null;

  const { candidates } = decision.data.payload;
  const standing = standingAnswerOf(answer.error);
  const submit = (key: string, body: AnswerDecisionBody) => {
    setPending(key);
    answer.mutate({ decisionId, body, runId }, { onSettled: () => setPending(null) });
  };

  return (
    <div className="rb-ask">
      <div className="run-under">
        <div className="src">{ONCHAIN_PICK_ASK}</div>
        {standing ? <div className="src">{standingAnswerLine(standing)}</div> : null}
        {answer.isError && !standing ? <ErrorLine error={answer.error} /> : null}
        <div className="candidates">
          {candidates.map((candidate) => (
            <EntityCard
              key={candidate.entity_id}
              candidate={candidate}
              working={pending === candidate.entity_id}
              disabled={pending !== null && pending !== candidate.entity_id}
              onChoose={() => submit(candidate.entity_id, { chosen_entity: candidate.entity_id })}
            />
          ))}
        </div>
        <div className="layer-actions">
          <WorkingButton
            label={ONCHAIN_PICK_NONE}
            workingLabel="Saving…"
            variant="act"
            working={pending === 'none'}
            disabled={pending !== null && pending !== 'none'}
            onClick={() =>
              submit('none', {
                none_of_the_entities: true,
                declined: candidates.map((c) => c.entity_id),
              })
            }
          />
        </div>
      </div>
    </div>
  );
}

/**
 * What kind of party the identity is, in plain words. The provider's own types
 * are short codes ("cex", "market-maker"); the common ones read as words, and
 * any other is printed as a word rather than dropped.
 */
const ENTITY_KINDS: Readonly<Record<string, string>> = {
  individual: 'A person',
  cex: 'An exchange',
  dex: 'A decentralized exchange',
  exchange: 'An exchange',
  fund: 'A fund',
  'market-maker': 'A market maker',
  otc: 'An over-the-counter desk',
  custodian: 'A custodian',
  dao: 'A DAO',
  'nft-marketplace': 'An NFT marketplace',
};

export function entityKindLine(type: string | null): string | null {
  if (!type) return null;
  const word = type.trim().toLowerCase();
  if (!word) return null;
  const plain = word.replace(/[_-]+/g, ' ');
  return ENTITY_KINDS[word] ?? plain.charAt(0).toUpperCase() + plain.slice(1);
}

const sentence = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

function EntityCard({
  candidate,
  working,
  disabled,
  onChoose,
}: {
  candidate: OnchainEntityCandidate;
  working: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  const kind = entityKindLine(candidate.entity_type);
  return (
    <div className="candidate">
      <div className="who">
        <div className="cname">
          {candidate.name}
          {candidate.recommended ? (
            <span className="tag-faint block">{ONCHAIN_PICK_RECOMMENDED}</span>
          ) : null}
        </div>
        {kind ? <div className="cdetail">{kind}</div> : null}
        {candidate.why ? <div className="cdetail">{sentence(candidate.why)}</div> : null}
        {candidate.links.map((link) => (
          <div className="cdetail" key={link}>
            <a className="link" href={link} target="_blank" rel="noopener noreferrer">
              {link.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          </div>
        ))}
      </div>
      <WorkingButton
        label={ONCHAIN_PICK_CHOOSE}
        workingLabel="Saving…"
        working={working}
        disabled={disabled}
        onClick={onChoose}
      />
    </div>
  );
}
